import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { ThemeContext, type Appearance } from './theme-context';

const STORAGE_KEY = 'appearance';

function applyTheme(value: Appearance): void {
    if (typeof document === 'undefined') return;
    if (value === 'system') {
        const dark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        document.documentElement.classList.toggle('dark', dark);
    } else {
        document.documentElement.classList.toggle('dark', value === 'dark');
    }
}

function setCookie(name: string, value: string, days = 365): void {
    if (typeof document === 'undefined') return;
    const maxAge = days * 24 * 60 * 60;
    document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
}

function readStored(): Appearance {
    if (typeof window === 'undefined') return 'light';
    return (localStorage.getItem(STORAGE_KEY) as Appearance | null) ?? 'light';
}

interface ThemeProviderProps {
    children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
    const [appearance, setState] = useState<Appearance>(() => readStored());

    // Apply on mount + whenever the value changes.
    useEffect(() => {
        applyTheme(appearance);
    }, [appearance]);

    // React to system theme flips while in 'system' mode.
    useEffect(() => {
        if (appearance !== 'system') return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => applyTheme('system');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [appearance]);

    const setAppearance = useCallback((value: Appearance) => {
        setState(value);
        localStorage.setItem(STORAGE_KEY, value);
        setCookie(STORAGE_KEY, value);
    }, []);

    return <ThemeContext.Provider value={{ appearance, setAppearance }}>{children}</ThemeContext.Provider>;
}
